// NOTE: This component is missing the Framer Motion variant which exists in the course platform, because I'm trying to keep things simple.
import React from 'react';
import { styled } from '@linaria/react';
import { Play } from 'lucide-react';
import { useSpring, animated } from 'react-spring';

import useBoundingBox from '@/hooks/use-bounding-box';

import ColorModeContainer from '@/components/ColorModeContainer';

import ButtonPrimary from '@/components/ButtonPlayful/ButtonPrimary';

const ELEM_SIZE = 55;

type Box = 'css' | 'rs';

interface Props {
  boxes: Box[];
}

function LinearVsRealSpringsDemo({ boxes }: Props) {
  const [side, setSide] = React.useState<'left' | 'right'>('left');

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const boundingBox = useBoundingBox(wrapperRef);

  const displacement = boundingBox?.width
    ? boundingBox.width - ELEM_SIZE - 12
    : 0;

  const x = side === 'left' ? 0 : displacement;

  const transform = `translateX(${x}px)`;

  const springStyles = useSpring({
    to: { x },
    config: {
      tension: 100,
      friction: 10,
      mass: 1,
      precision: 0.0001,
    },
  });

  return (
    <Wrapper colorMode="dark" data-can-be-annotated={false}>
      <ContentOuterWrapper>
        <ContentWrapper ref={wrapperRef}>
          {boxes.includes('css') && (
            <>
              <Label>
                Using <em>linear()</em>:
              </Label>
              <BoxWrapper>
                <BackgroundBoxLeft />
                <BackgroundBoxRight />
                <CssBox
                  style={{
                    transform,
                  }}
                />
              </BoxWrapper>
            </>
          )}

          {boxes.includes('rs') && (
            <>
              <Label>
                Using <em>React Spring</em>:
              </Label>
              <BoxWrapper>
                <BackgroundBoxLeft />
                <BackgroundBoxRight />
                <RSBox style={springStyles} />
              </BoxWrapper>
            </>
          )}
        </ContentWrapper>
      </ContentOuterWrapper>
      <Footer>
        <Button
          Icon={Play}
          onClick={() => setSide(side === 'left' ? 'right' : 'left')}
        >
          Run Animation
        </Button>
      </Footer>
    </Wrapper>
  );
}

const Wrapper = styled(ColorModeContainer)`
  border: 1px solid var(--color-gray-300);
  border-radius: 8px;
  margin-bottom: 64px;

  @media (max-width: 25rem) {
    margin-inline: calc(var(--viewport-padding) * -1);
  }
`;

const ContentOuterWrapper = styled.div`
  padding-inline: 16px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 300px;
  margin-inline: auto;
  padding-block: 32px;
`;

const Label = styled.div`
  font-size: 1rem;
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-mono);
`;

const BoxWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  border: 2px solid var(--color-gray-300);
  border-radius: 8px;
  padding: 4px;
`;

const RSBox = styled(animated.div)`
  position: relative;
  width: ${ELEM_SIZE}px;
  height: ${ELEM_SIZE}px;
  background-color: var(--color-text);
  border-radius: 4px;
`;

const CssBox = styled.div`
  /* prettier-ignore */
  --spring-easing: linear( 0, 0.033 1.5%, 0.142 3.3%, 0.726 9.6%, 0.946 12.5%, 1.087 15.4%, 1.13 16.9%, 1.155 18.5%, 1.163 20.3%, 1.152 22.3%, 1.015 32.1%, 0.988 35.2%, 0.975 38.4%, 0.975 42.4%, 1.004 58.1%, 1);
  --spring-duration: 1750ms;
  width: ${ELEM_SIZE}px;
  height: ${ELEM_SIZE}px;
  background-color: var(--color-text);
  border-radius: 4px;
  transition: transform var(--spring-easing) var(--spring-duration);
`;

const BackgroundBox = styled.div`
  position: absolute;
  top: 4px;
  width: ${ELEM_SIZE}px;
  height: ${ELEM_SIZE}px;
  border-radius: 4px;
  border: 4px dashed var(--color-gray-300);
  opacity: 0.5;
`;

const BackgroundBoxLeft = styled(BackgroundBox)`
  left: 4px;
`;

const BackgroundBoxRight = styled(BackgroundBox)`
  right: 4px;
`;

const Button = styled(ButtonPrimary)`
  width: 100%;
  max-width: 300px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
  border-top: 1px solid var(--color-gray-300);
`;

export default LinearVsRealSpringsDemo;
